import { useMemo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TableComponent from "../../components/Table/Table";
import Loading from "../../components/Loading/Loading";
import ActionButton from "../../components/Button/ActionButton";
import RegisterAccountButton from "../../components/payments/RegisterAccountButton/RegisterAccountButton";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import ConfigurationRow from "./ConfigurationRow";
import { tableHeaders } from "./utils";
import usePaymentConfigurations from "../../hooks/usePaymentConfigurations";
import { PaymentConfiguration } from "../../types/payments";
import { PAYMENTS_HELP_CONTENT, PAYMENTS_MANUAL_URL } from "../../constants";
import { semanticColors } from "../../utils/colors";
import { pageTitleSx, pageHeaderRowSx } from "../../utils/pageStyles";
import { configurationPath } from "../../utils/paymentsRoutes";

/** This screen also carries a refresh action beside the title. */
const listHeaderRowSx = { ...pageHeaderRowSx, gap: 1 };

/**
 * Entry point of the payment account setup: lists the professional's payment
 * configurations and starts the registration of a new one.
 *
 * There is no delete action by design — the mobile app shows a swipe to delete
 * that is wired to a no-op, so the API has no supported removal.
 */
export default function PaymentConfigurationsList() {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    configurations,
    loading,
    error,
    refetch
  } = usePaymentConfigurations();
  const headers = useMemo(() => tableHeaders(t), [t]);

  const openConfiguration = (configuration: PaymentConfiguration) =>
    history.push(configurationPath(configuration.id));

  return (
    <Box
      sx={{
        px: { xs: "16px", sm: "24px" },
        py: "48px",
        display: "flex",
        flexDirection: "column",
        gap: "24px"
      }}
    >
      <Box sx={listHeaderRowSx}>
        <Typography sx={pageTitleSx}>
          {t("paymentConfigurations.title")}
        </Typography>
        <HelpButton linkName={PAYMENTS_HELP_CONTENT.LIST_CONFIG} />
        {/* Easypay moves the review state on its own, with no webhook to tell
            us, so an explicit refresh is offered alongside the focus refetch. */}
        <ActionButton
          label={t("paymentConfigurations.refresh")}
          onClick={refetch}
          variant="secondary"
        />
      </Box>

      {/* The spinner only replaces the list while there is no list yet: on the
          focus refetch the rows stay put and update in place. */}
      {loading && configurations.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
          <Loading />
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2
          }}
        >
          <Typography sx={{ color: semanticColors.text.primary }}>
            {t("paymentConfigurations.loadError")}
          </Typography>
          <ActionButton
            label={t("paymentConfigurations.retry")}
            onClick={refetch}
            variant="secondary"
          />
        </Box>
      ) : configurations.length > 0 ? (
        <TableComponent headerCfg={headers}>
          {configurations.map(configuration => (
            <ConfigurationRow
              key={configuration.id}
              configuration={configuration}
              onSelect={openConfiguration}
            />
          ))}
        </TableComponent>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2
          }}
        >
          <Typography sx={{ color: semanticColors.text.primary }}>
            {t("paymentConfigurations.empty")}
          </Typography>
          <RegisterAccountButton />
          {/* The mobile app opens this PDF on its own after 1.5s when the list
              is empty. Browsers block that, so it stays an explicit link. */}
          <Link
            href={PAYMENTS_MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("paymentConfigurations.openManual")}
          </Link>
        </Box>
      )}

      {configurations.length > 0 && (
        <Box display="flex" justifyContent="center">
          <RegisterAccountButton />
        </Box>
      )}
    </Box>
  );
}
